import { redirect, RouteObject } from "react-router-dom";
import { Layout } from "../Layout/Layout";
import React from "react";
import { GAME_TYPE_ALIASES } from "../Pages/CustomGame/gameAliases";

const V3GamesPage = React.lazy(
  async () => await import("../Pages/CustomGame/V3GamePages")
);

const CustomEntityPage = React.lazy(
  async () => await import("../Pages/CustomGame/Database/EntityPage")
);

const V3GameProfilePage = React.lazy(
  async () => await import("../Pages/CustomGame/Profile/ProfilePage")
);

const V3GameUnlockedSettings = React.lazy(
  async () => await import('../Pages/CustomGame/UnlockedSettings/UnlockedSettings')
);

const V5GameUnlockedSettings = React.lazy(
  async () => await import('../Pages/CustomGame/V5/V5GameUnlockedSettings')
);

const MissionEditorPage = React.lazy(
  async () => await import('../Pages/CustomGame/Missions/MissionEditorPage')
);

const LootBoxV2EditorPage = React.lazy(
  async () => await import('../Pages/CustomGame/LootBoxV2/LootBoxV2EditorPage')
);

const JackpotsV2EditorPage = React.lazy(
  async () => await import('../Pages/CustomGame/JackpotsV2/JackpotsV2EditorPage')
);

const ShopV2EditorPage = React.lazy(
  async () => await import('../Pages/CustomGame/ShopV2/ShopV2EditorPage')
);

const MissionCreatePage = React.lazy(
  async () => await import('../Pages/CustomGame/Missions/MissionCreatePage')
);

const ProfileDetailPage = React.lazy(
  async () => await import('../Pages/CustomGame/Profile/ProfileDetailPage')
);

const CustomEntityComponent = React.lazy(async () => {
  const mod = await import('../Components/CustomGame/CustomEntity');
  return { default: mod.CustomEntityComponent };
});

// Editor routes for every loot-box variant, generated from the alias map so a new variant
// (added to GAME_TYPE_ALIASES) needs no router change.
const lootBoxVariantEditorRoutes: RouteObject[] = Object.keys(GAME_TYPE_ALIASES)
  .filter((variant) => GAME_TYPE_ALIASES[variant] === "lootBoxV2")
  .flatMap((variant) => [
    {
      path: `v5/game/${variant}/profile/:profileId/game/:id/edit`,
      element: (
        <Layout>
          <LootBoxV2EditorPage systemName={variant} />
        </Layout>
      ),
    },
    {
      path: `game/${variant}/:id/edit`,
      element: (
        <Layout>
          <LootBoxV2EditorPage systemName={variant} />
        </Layout>
      ),
    },
  ]);

export const RouterCusomGame: RouteObject[] = [
  // ── Missions: dedicated editor (must be before generic :systemName routes)
  {
    path: "game/missions/:id/edit",
    element: (
      <Layout>
        <MissionEditorPage />
      </Layout>
    ),
  },
  {
    // Profile-aware mission editor: /v3/game/missions/profile/:profileId/game/:id/edit
    path: "v3/game/missions/profile/:profileId/game/:id/edit",
    element: (
      <Layout>
        <MissionEditorPage />
      </Layout>
    ),
  },
  {
    // Profile-aware mission editor: /v5/game/missions/profile/:profileId/game/:id/edit
    path: "v5/game/missions/profile/:profileId/game/:id/edit",
    element: (
      <Layout>
        <MissionEditorPage />
      </Layout>
    ),
  },
  {
    // Must be before game/missions/:id — otherwise "create" is treated as an id.
    // Uses CustomEntityComponent directly to hardcode systemName="missions"
    // since this is a fixed path with no :systemName param.
    path: "game/missions/create",
    element: (
      <Layout>
        <CustomEntityComponent systemName="missions" initialTab="global" />
      </Layout>
    ),
  },
  {
    // Profile-aware create: /v3/game/missions/profile/:profileId/game/create
    path: "v3/game/missions/profile/:profileId/game/create",
    element: (
      <Layout>
        <MissionCreatePage />
      </Layout>
    ),
  },
  {
    // Profile-aware create: /v5/game/missions/profile/:profileId/game/create
    path: "v5/game/missions/profile/:profileId/game/create",
    element: (
      <Layout>
        <MissionCreatePage />
      </Layout>
    ),
  },
  {
    // Must be before game/missions/:id — equal score (23pts), order wins
    path: "game/:systemName/list",
    element: (
      <Layout>
        <V3GamesPage />
      </Layout>
    ),
  },
  {
    // Redirect /game/missions/:id → /game/missions/:id/edit
    path: "game/missions/:id",
    loader: async ({ params }) => redirect(`/game/missions/${params.id}/edit`),
  },
  {
    path: "game/:systemName/",
    loader: async ({ params }) => {
      return redirect(`/game/${params.systemName}/list`);
    },
  },
  {
    path: "game/:systemName/create",
    element: (
      <Layout>
        <CustomEntityPage />
      </Layout>
    ),
  },
  {
    path: "game/:systemName/:id/",
    loader: async ({ params }) => {
      const tab = "global";
      return redirect(`/game/${params.systemName}/${tab}`);
    },
  },
  {
    path: "game/:systemName/:id/:tab",
    element: (
      <Layout>
        <CustomEntityPage />
      </Layout>
    ),
  },
  {
    path: "game/:systemName/profile/",
    loader: async ({ params }) => {
      return redirect(`/game/${params.systemName}/profile/list`);
    },
  },
  {
    path: "game/:systemName/profile/list",
    element: (
      <Layout>
        <V3GamesPage />
      </Layout>
    ),
  },
  {
    path: "game/:systemName/profile/create",
    element: (
      <Layout>
        <V3GameProfilePage />
      </Layout>
    ),
  },
  {
    path: "game/:systemName/profile/:id/",
    loader: async ({ params }) => {
      const tab = "global";
      return redirect(`/game/${params.systemName}/profile/${tab}`);
    },
  },
  {
    path: "game/:systemName/profile/:id/:tab",
    element: (
      <Layout>
        <V3GameProfilePage />
      </Layout>
    ),
  },
  {
    // Must be before v3/game/:systemName (more specific wins)
    path: "v3/game/:systemName/profile/:id",
    element: (
      <Layout>
        <ProfileDetailPage />
      </Layout>
    ),
  },
  {
    path: "v3/game/:systemName",
    element: (
      <Layout>
        <V3GameUnlockedSettings />
      </Layout>
    ),
  },
  // ── LootBoxV2 editor ─────────────────────────────────────────────────────────
  {
    path: "v5/game/lootBoxV2/profile/:profileId/game/:id/edit",
    element: (
      <Layout>
        <LootBoxV2EditorPage />
      </Layout>
    ),
  },
  {
    path: "game/lootBoxV2/:id/edit",
    element: (
      <Layout>
        <LootBoxV2EditorPage />
      </Layout>
    ),
  },
  // ── LootBoxV2 React-variant editors (generated from the alias map) ──
  ...lootBoxVariantEditorRoutes,
  // ── JackpotsV2 editor ────────────────────────────────────────────────────────
  {
    path: "v5/game/jackpotsV2/profile/:profileId/game/:id/edit",
    element: (
      <Layout>
        <JackpotsV2EditorPage />
      </Layout>
    ),
  },
  {
    path: "game/jackpotsV2/:id/edit",
    element: (
      <Layout>
        <JackpotsV2EditorPage />
      </Layout>
    ),
  },
  // ── JackpotsV3 editor — the V2 editor with the V3 fields ──────────────────────
  {
    path: "v5/game/jackpotsV3/profile/:profileId/game/:id/edit",
    element: (
      <Layout>
        <JackpotsV2EditorPage systemName="jackpotsV3" />
      </Layout>
    ),
  },
  {
    path: "game/jackpotsV3/:id/edit",
    element: (
      <Layout>
        <JackpotsV2EditorPage systemName="jackpotsV3" />
      </Layout>
    ),
  },
  // ── ShopV2 editor ────────────────────────────────────────────────────────────
  {
    path: "v5/game/shopV2/profile/:profileId/game/:id/edit",
    element: (
      <Layout>
        <ShopV2EditorPage />
      </Layout>
    ),
  },
  {
    path: "game/shopV2/:id/edit",
    element: (
      <Layout>
        <ShopV2EditorPage />
      </Layout>
    ),
  },
  // ── V5 routes ────────────────────────────────────────────────────────────────
  {
    // Must be before v5/game/:systemName (more specific wins)
    path: "v5/game/:systemName/profile/:id",
    element: (
      <Layout>
        <ProfileDetailPage />
      </Layout>
    ),
  },
  {
    path: "v5/game/:systemName",
    element: (
      <Layout>
        <V5GameUnlockedSettings />
      </Layout>
    ),
  },
];
